/* ============================================================================
 * search.js — SearchOverlay behaviour
 *
 * Vanilla JS. Concatenated into assets/built/source.js by gulp.
 *
 * Responsibilities:
 *   • Open on "/" key (global, unless focus is inside an input/textarea/select
 *     or a contenteditable element)
 *   • Close on Esc, backdrop click, or the dedicated close control
 *   • Debounced query against Ghost Content API (same-origin)
 *   • Render results as <li role="option"> with prefix + title
 *   • Keyboard navigation of results with ↑ / ↓ / Enter
 *   • Disabled state when no API key is configured
 *
 * Constitutional basis: H-AX-01 (sovereignty — no external script dep),
 *                       I-AX-03 (privacy — same-origin), H-AX-02 (clarity).
 * ============================================================================ */

;(function () {
  'use strict'

  var overlay = document.querySelector('[data-sn-search-overlay]')
  if (!overlay) return

  var apiKey = overlay.getAttribute('data-api-key') || ''
  var apiUrl = overlay.getAttribute('data-api-url') || ''
  var input = overlay.querySelector('[data-sn-search-input]')
  var resultsList = overlay.querySelector('[data-sn-search-results]')
  var emptyEl = overlay.querySelector('[data-sn-search-empty]')
  var disabledEl = overlay.querySelector('[data-sn-search-disabled]')
  var closeEls = overlay.querySelectorAll('[data-sn-search-close]')
  var closeBtn = overlay.querySelector('.sn-search-overlay__close')

  var selectedIndex = -1
  var debounceTimer = null
  var lastRequestId = 0
  var lastFocusedBeforeOpen = null

  // ────────────────────────────────────────────────────────────────────
  // Open / close + focus management
  // ────────────────────────────────────────────────────────────────────
  function openOverlay() {
    lastFocusedBeforeOpen = document.activeElement
    overlay.hidden = false
    document.documentElement.classList.add('sn-search-open')
    if (!apiKey) {
      disabledEl.hidden = false
    } else {
      disabledEl.hidden = true
    }
    // Defer focus to after `hidden` is removed so it's actually focusable.
    window.requestAnimationFrame(function () {
      input.focus()
      input.select()
    })
  }

  function closeOverlay() {
    overlay.hidden = true
    document.documentElement.classList.remove('sn-search-open')
    input.value = ''
    resultsList.innerHTML = ''
    emptyEl.hidden = true
    selectedIndex = -1
    // Return focus to wherever it was before we opened.
    if (lastFocusedBeforeOpen && typeof lastFocusedBeforeOpen.focus === 'function') {
      lastFocusedBeforeOpen.focus()
    }
    lastFocusedBeforeOpen = null
  }

  // ────────────────────────────────────────────────────────────────────
  // Focus trap — tab/shift-tab wraps between the three focusable surfaces
  // inside the overlay: close button, input, and result anchors.
  // ────────────────────────────────────────────────────────────────────
  function getFocusableItems() {
    var items = []
    if (closeBtn) items.push(closeBtn)
    items.push(input)
    var resultAnchors = resultsList.querySelectorAll('a')
    resultAnchors.forEach(function (a) {
      items.push(a)
    })
    return items
  }

  function handleFocusTrap(event) {
    if (event.key !== 'Tab') return
    var items = getFocusableItems()
    if (!items.length) return
    var first = items[0]
    var last = items[items.length - 1]
    var active = document.activeElement
    if (event.shiftKey && active === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && active === last) {
      event.preventDefault()
      first.focus()
    }
  }

  function isEditableTarget(el) {
    if (!el) return false
    var tag = (el.tagName || '').toUpperCase()
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
    if (el.isContentEditable) return true
    return false
  }

  // ────────────────────────────────────────────────────────────────────
  // Global shortcut handler
  // ────────────────────────────────────────────────────────────────────
  document.addEventListener('keydown', function (event) {
    // Open: "/" key, unless user is typing in an input/textarea.
    if (event.key === '/' && !isEditableTarget(event.target) && overlay.hidden) {
      event.preventDefault()
      openOverlay()
      return
    }

    // Close: Esc, only if overlay is open.
    if (event.key === 'Escape' && !overlay.hidden) {
      event.preventDefault()
      closeOverlay()
      return
    }

    // In-overlay navigation (only when overlay is active and not typing in input).
    if (overlay.hidden) return

    if (event.key === 'Tab') {
      handleFocusTrap(event)
      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      moveSelection(1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      moveSelection(-1)
    } else if (event.key === 'Enter') {
      var items = resultsList.querySelectorAll('li')
      if (selectedIndex >= 0 && items[selectedIndex]) {
        var a = items[selectedIndex].querySelector('a')
        if (a && a.href) {
          event.preventDefault()
          window.location.href = a.href
        }
      }
    }
  })

  closeEls.forEach(function (el) {
    el.addEventListener('click', closeOverlay)
  })

  // ────────────────────────────────────────────────────────────────────
  // Results selection (highlight with aria-selected)
  // ────────────────────────────────────────────────────────────────────
  function moveSelection(delta) {
    var items = resultsList.querySelectorAll('li')
    if (!items.length) return
    if (selectedIndex >= 0 && items[selectedIndex]) {
      items[selectedIndex].removeAttribute('aria-selected')
    }
    selectedIndex = Math.max(0, Math.min(items.length - 1, selectedIndex + delta))
    var next = items[selectedIndex]
    if (next) {
      next.setAttribute('aria-selected', 'true')
      next.scrollIntoView({ block: 'nearest' })
    }
  }

  // ────────────────────────────────────────────────────────────────────
  // Query + render
  // ────────────────────────────────────────────────────────────────────
  function escapeHTML(str) {
    if (typeof str !== 'string') return ''
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  function prefixFromTag(post) {
    var slug = post && post.primary_tag && post.primary_tag.slug
    if (slug === 'notes') return 'NOTE'
    if (slug === 'briefs') return 'BRIEF'
    return 'REVIEW'
  }

  function buildQueryUrl(query) {
    // Ghost Content API filter syntax uses ~ for substring match (case-insensitive).
    // A single quote inside the query would break the filter; escape by doubling
    // single quotes (Ghost NQL convention).
    var safeQuery = query.replace(/'/g, "''")
    var filter = "title:~'" + safeQuery + "',custom_excerpt:~'" + safeQuery + "'"
    var url =
      apiUrl +
      '?key=' +
      encodeURIComponent(apiKey) +
      '&filter=' +
      encodeURIComponent(filter) +
      '&limit=10' +
      '&fields=title,url,primary_tag' +
      '&include=tags'
    return url
  }

  function runQuery(query) {
    if (!apiKey) {
      disabledEl.hidden = false
      resultsList.innerHTML = ''
      emptyEl.hidden = true
      return
    }
    if (!query || query.length < 2) {
      resultsList.innerHTML = ''
      emptyEl.hidden = true
      selectedIndex = -1
      return
    }

    var requestId = ++lastRequestId

    fetch(buildQueryUrl(query), { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status)
        return res.json()
      })
      .then(function (payload) {
        if (requestId !== lastRequestId) return // stale
        renderResults(payload.posts || [])
      })
      .catch(function () {
        if (requestId !== lastRequestId) return
        // Silent failure — render no results rather than a modal error.
        renderResults([])
      })
  }

  function renderResults(posts) {
    resultsList.innerHTML = ''
    selectedIndex = -1

    if (!posts.length) {
      emptyEl.hidden = false
      return
    }
    emptyEl.hidden = true

    posts.forEach(function (post) {
      var li = document.createElement('li')
      li.setAttribute('role', 'option')
      li.innerHTML =
        '<a href="' +
        escapeHTML(post.url) +
        '">' +
        '<span class="sn-search-overlay__result-prefix">' +
        prefixFromTag(post) +
        '</span>' +
        '<span class="sn-search-overlay__result-title">' +
        escapeHTML(post.title || '') +
        '</span>' +
        '</a>'
      resultsList.appendChild(li)
    })
  }

  // ────────────────────────────────────────────────────────────────────
  // Input listener (debounced)
  // ────────────────────────────────────────────────────────────────────
  input.addEventListener('input', function () {
    var q = input.value.trim()
    window.clearTimeout(debounceTimer)
    debounceTimer = window.setTimeout(function () {
      runQuery(q)
    }, 150)
  })
})()
